<script setup>
import { reactive, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import { useMapGetter, useStore } from 'dashboard/composables/store';

import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import TagMultiSelectComboBox from 'dashboard/components-next/combobox/TagMultiSelectComboBox.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import EmailTemplateVariableInfo from 'dashboard/components-next/Campaigns/Pages/CampaignPage/EmailCampaign/EmailTemplateVariableInfo.vue';

const emit = defineEmits(['submit', 'cancel']);

const { t } = useI18n();
const store = useStore();

const formState = {
  uiFlags: useMapGetter('campaigns/getUIFlags'),
  labels: useMapGetter('labels/getLabels'),
  inboxes: useMapGetter('inboxes/getEmailInboxes'),
  cannedResponses: useMapGetter('cannedResponse/getCannedResponses'),
};

onMounted(async () => {
  // Load canned responses
  try {
    await store.dispatch('cannedResponse/get');
  } catch (error) {
    console.error('Failed to load canned responses:', error);
  }
});

const initialState = {
  title: '',
  subject: '',
  message: '',
  inboxId: null,
  scheduledAt: null,
  selectedAudience: [],
  templateId: null,
  sendMode: 'scheduled',
  variableValues: {},
};

const state = reactive({ ...initialState });

const rules = computed(() => ({
  title: { required, minLength: minLength(1) },
  subject: { required, minLength: minLength(1) },
  // Message is optional - can be filled from template or left empty
  inboxId: { required },
  scheduledAt: state.sendMode === 'scheduled' ? { required } : {},
  selectedAudience: { required },
}));

const v$ = useVuelidate(rules, state);

const sendModeOptions = [
  { value: 'immediate', label: 'Send immediately' },
  { value: 'scheduled', label: 'Schedule for later' },
];

const isCreating = computed(() => formState.uiFlags.value.isCreating);

const currentDateTime = computed(() => {
  // Added to disable the scheduled at field from being set to the current time
  const now = new Date();
  const localTime = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
  return localTime.toISOString().slice(0, 16);
});

const mapToOptions = (items, valueKey, labelKey) =>
  items?.map(item => ({
    value: item[valueKey],
    label: item[labelKey],
  })) ?? [];

const audienceList = computed(() =>
  mapToOptions(formState.labels.value, 'id', 'title')
);

const inboxOptions = computed(() =>
  mapToOptions(formState.inboxes.value, 'id', 'name')
);

// Canned response template options - filter to include text and multimedia (exclude interactive)
const templateOptions = computed(() => {
  const responses = formState.cannedResponses.value || [];
  console.log('[EMAIL CAMPAIGN] All canned responses:', responses);

  // Exclude only interactive types (input_select, cards, catalog_product, etc.)
  const interactiveTypes = [
    'input_select',
    'cards',
    'catalog_product',
    'catalog_product_list',
    'flow',
  ];
  const filteredResponses = responses.filter(response => {
    const isAllowed = !interactiveTypes.includes(response.content_type);
    console.log(
      `[EMAIL CAMPAIGN] Response ${response.short_code}: content_type="${response.content_type}", included=${isAllowed}`
    );
    return isAllowed;
  });

  console.log('[EMAIL CAMPAIGN] Filtered responses:', filteredResponses);

  const options = filteredResponses.map(response => ({
    value: response.id,
    label: response.short_code,
    content: response.content,
  }));
  // Add a "No template" option at the beginning
  return [{ value: null, label: 'No template', content: '' }, ...options];
});

// When template is selected, update the message
const handleTemplateChange = templateId => {
  if (templateId) {
    const responses = formState.cannedResponses.value || [];
    const template = responses.find(r => r.id === templateId);
    if (template) {
      state.message = template.content;
    }
  } else {
    state.message = '';
  }
};

// Handle variable values update from EmailTemplateVariableInfo
const handleVariableValuesUpdate = values => {
  state.variableValues = values;
};

const getErrorMessage = (field, errorKey) => {
  const baseKey = 'CAMPAIGN.EMAIL.CREATE.FORM';
  return v$.value[field].$error ? t(`${baseKey}.${errorKey}.ERROR`) : '';
};

const formErrors = computed(() => ({
  title: getErrorMessage('title', 'TITLE'),
  subject: getErrorMessage('subject', 'SUBJECT'),
  inbox: getErrorMessage('inboxId', 'INBOX'),
  scheduledAt: getErrorMessage('scheduledAt', 'SCHEDULED_AT'),
  audience: getErrorMessage('selectedAudience', 'AUDIENCE'),
}));

const isSubmitDisabled = computed(() => v$.value.$invalid);

const formatToUTCString = localDateTime =>
  localDateTime ? new Date(localDateTime).toISOString() : null;

const resetState = () => {
  Object.assign(state, initialState);
};

const handleCancel = () => emit('cancel');

const prepareCampaignDetails = () => {
  const campaignData = {
    title: state.title,
    subject: state.subject,
    message: state.message,
    inbox_id: state.inboxId,
    send_mode: state.sendMode,
    audience: {
      label_ids: state.selectedAudience,
    },
  };

  // Include variable values if any are provided
  if (Object.keys(state.variableValues).length > 0) {
    campaignData.variable_values = state.variableValues;
  }

  // Only include scheduled_at if mode is scheduled
  if (state.sendMode === 'scheduled') {
    campaignData.scheduled_at = formatToUTCString(state.scheduledAt);
  } else {
    campaignData.scheduled_at = new Date().toISOString();
  }

  return campaignData;
};

const handleSubmit = async () => {
  const isFormValid = await v$.value.$validate();
  if (!isFormValid) return;

  emit('submit', prepareCampaignDetails());
  resetState();
  handleCancel();
};
</script>

<template>
  <form class="flex flex-col gap-4" @submit.prevent="handleSubmit">
    <Input
      v-model="state.title"
      :label="t('CAMPAIGN.EMAIL.CREATE.FORM.TITLE.LABEL')"
      :placeholder="t('CAMPAIGN.EMAIL.CREATE.FORM.TITLE.PLACEHOLDER')"
      :message="formErrors.title"
      :message-type="formErrors.title ? 'error' : 'info'"
    />

    <Input
      v-model="state.subject"
      :label="t('CAMPAIGN.EMAIL.CREATE.FORM.SUBJECT.LABEL')"
      :placeholder="t('CAMPAIGN.EMAIL.CREATE.FORM.SUBJECT.PLACEHOLDER')"
      :message="formErrors.subject"
      :message-type="formErrors.subject ? 'error' : 'info'"
    />

    <div class="flex flex-col gap-1">
      <label for="template" class="mb-0.5 text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.EMAIL.CREATE.FORM.TEMPLATE.LABEL') }}
      </label>
      <ComboBox
        id="template"
        v-model="state.templateId"
        :options="templateOptions"
        :placeholder="t('CAMPAIGN.EMAIL.CREATE.FORM.TEMPLATE.PLACEHOLDER')"
        class="[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak [&>div>button:not(.focused)]:hover:!outline-n-slate-6"
        @update:model-value="handleTemplateChange"
      />
      <span class="text-xs text-n-slate-10 mt-1">
        {{ t('CAMPAIGN.EMAIL.CREATE.FORM.TEMPLATE.INFO') }}
      </span>
    </div>

    <!-- <TextArea
      v-model="state.message"
      :label="t('CAMPAIGN.EMAIL.CREATE.FORM.MESSAGE.LABEL')"
      :placeholder="t('CAMPAIGN.EMAIL.CREATE.FORM.MESSAGE.PLACEHOLDER')"
      show-character-count
      :max-length="20000"
    />

    <EmailTemplateVariableInfo
      :message="state.message"
      @update:variableValues="handleVariableValuesUpdate"
    /> -->

    <div class="flex flex-col gap-1">
      <label for="inbox" class="mb-0.5 text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.EMAIL.CREATE.FORM.INBOX.LABEL') }}
      </label>
      <ComboBox
        id="inbox"
        v-model="state.inboxId"
        :options="inboxOptions"
        :has-error="!!formErrors.inbox"
        :placeholder="t('CAMPAIGN.EMAIL.CREATE.FORM.INBOX.PLACEHOLDER')"
        :message="formErrors.inbox"
        class="[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak [&>div>button:not(.focused)]:hover:!outline-n-slate-6"
      />
    </div>

    <div class="flex flex-col gap-1">
      <label for="audience" class="mb-0.5 text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.EMAIL.CREATE.FORM.AUDIENCE.LABEL') }}
      </label>
      <TagMultiSelectComboBox
        v-model="state.selectedAudience"
        :options="audienceList"
        :label="t('CAMPAIGN.EMAIL.CREATE.FORM.AUDIENCE.LABEL')"
        :placeholder="t('CAMPAIGN.EMAIL.CREATE.FORM.AUDIENCE.PLACEHOLDER')"
        :has-error="!!formErrors.audience"
        :message="formErrors.audience"
        class="[&>div>button]:bg-n-alpha-black2"
      />
    </div>

    <div class="flex flex-col gap-1">
      <label for="sendMode" class="mb-0.5 text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.EMAIL.CREATE.FORM.SEND_MODE.LABEL') }}
      </label>
      <ComboBox
        id="sendMode"
        v-model="state.sendMode"
        :options="sendModeOptions"
        class="[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak [&>div>button:not(.focused)]:hover:!outline-n-slate-6"
      />
    </div>

    <Input
      v-if="state.sendMode === 'scheduled'"
      v-model="state.scheduledAt"
      :label="t('CAMPAIGN.EMAIL.CREATE.FORM.SCHEDULED_AT.LABEL')"
      type="datetime-local"
      :min="currentDateTime"
      :placeholder="t('CAMPAIGN.EMAIL.CREATE.FORM.SCHEDULED_AT.PLACEHOLDER')"
      :message="formErrors.scheduledAt"
      :message-type="formErrors.scheduledAt ? 'error' : 'info'"
    />

    <div class="flex items-center justify-between w-full gap-3">
      <Button
        variant="faded"
        color="slate"
        type="button"
        :label="t('CAMPAIGN.EMAIL.CREATE.FORM.BUTTONS.CANCEL')"
        class="w-full bg-n-alpha-2 text-n-blue-text hover:bg-n-alpha-3"
        @click="handleCancel"
      />
      <Button
        :label="t('CAMPAIGN.EMAIL.CREATE.FORM.BUTTONS.CREATE')"
        class="w-full"
        type="submit"
        :is-loading="isCreating"
        :disabled="isCreating || isSubmitDisabled"
      />
    </div>
  </form>
</template>
